import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lightning, Clock, MagicWand } from "@phosphor-icons/react";

export function AutomationTracksSection() {
  const { t } = useTranslation(["newLanding"]);
  const containerRef = useRef<HTMLElement | null>(null);
  const [started, setStarted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  // No connector lines; only pill shapes remain

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handle = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener("change", handle);
    return () => mq.removeEventListener("change", handle);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    if (reducedMotion) {
      setStarted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(containerRef.current);
    return () => io.disconnect();
  }, [reducedMotion]);

  // All connector calculations removed

  const manualStepKeys = [
    "topic",
    "research",
    "create_activities",
    "add_extra_information",
    "draft_lesson_plan",
    "write_final_version",
    "create_questions",
    "draft_worksheet",
    "style_worksheet",
    "create_tasks",
    "draft_homework",
    "style_homework",
  ] as const;
  // Other AI mirrors manual steps; AI-assisted ones are highlighted below

  const teacherToolsMidKeys = [
    "lesson_lab",
    "writing_wizard",
    "resource_refinery",
  ] as const;

  type Track = {
    key: "manual" | "otherAI" | "teacherTools";
    icon: JSX.Element;
    minutes: number;
    steps: number;
    colorClass: string;
  };

  const tracks: Track[] = useMemo(
    () => [
      {
        key: "manual",
        icon: (
          <Clock size={22} weight="bold" className="text-muted-foreground" />
        ),
        minutes: 180,
        steps: manualStepKeys.length,
        colorClass: "bg-primary",
      },
      {
        key: "otherAI",
        icon: <MagicWand size={22} weight="bold" className="text-primary" />,
        minutes: 45,
        steps: manualStepKeys.length,
        colorClass: "bg-primary",
      },
      {
        key: "teacherTools",
        icon: <Lightning size={22} weight="bold" className="text-dark-green" />,
        minutes: 3,
        steps: 2,
        colorClass: "bg-dark-green",
      },
    ],
    []
  );

  const aiAssistedKeys = useMemo(
    () =>
      new Set<string>([
        // AI-assisted steps for Other AI tools
        "research",
        "create_activities",
        "create_questions",
        "create_tasks",
      ]),
    []
  );

  const savedPercent = useMemo(() => {
    const base = tracks.find((t) => t.key === "manual")!.minutes;
    const tt = tracks.find((t) => t.key === "teacherTools")!.minutes;
    return Math.round(((base - tt) / base) * 100);
  }, [tracks]);

  return (
    <section
      ref={(el) => (containerRef.current = el)}
      className="flex flex-col items-center w-full px-4 py-12 sm:py-16 md:py-20"
      aria-label={t("tracks.aria")}
    >
      <Lightning className="mb-6 text-dark-green" size={40} weight="bold" />
      <h2 className="mb-3 text-2xl font-semibold tracking-tight text-center sm:text-3xl md:text-4xl">
        {t("tracks.title")}
      </h2>
      <p className="max-w-2xl mb-10 text-sm leading-relaxed text-center text-muted-foreground sm:text-base">
        {t("tracks.subtitle")}
      </p>

      <div className="grid w-full max-w-5xl grid-cols-1 gap-5 md:gap-6 md:grid-cols-3">
        {tracks.map((track) => {
          const isTT = track.key === "teacherTools";
          const cardClass = isTT
            ? "bg-dark-green text-primary-foreground border-dark-green shadow-xl ring-1 ring-dark-green/40 hover:shadow-2xl hover:-translate-y-0.5 transition"
            : "bg-card border-border";
          const railBg = isTT ? "bg-white/20" : "bg-muted/50";
          const progressClass = isTT ? "bg-white" : track.colorClass;
          const pillClass = isTT
            ? "border border-white/40 bg-white/10 text-primary-foreground/90"
            : "border border-border/70 bg-background/80 text-muted-foreground";

          return (
            <article
              key={track.key}
              className={
                "flex flex-col gap-4 p-5 border rounded-2xl " + cardClass
              }
            >
              <header className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {isTT ? (
                    <Lightning
                      size={22}
                      weight="bold"
                      className="text-primary-foreground"
                    />
                  ) : (
                    track.icon
                  )}
                  <h3 className="text-base font-semibold whitespace-nowrap">
                    {t(`tracks.${track.key}.heading`)}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={
                      "inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full " +
                      (isTT
                        ? "bg-white/10 text-primary-foreground border border-white/20"
                        : "bg-muted text-muted-foreground")
                    }
                  >
                    <Clock size={16} weight="bold" />
                    {t(`tracks.timeApprox.${track.key}`)}
                  </span>
                </div>
              </header>

              <div className="flex flex-col h-full gap-3 mt-1">
                <div
                  className={
                    "relative h-2 overflow-hidden rounded-full " + railBg
                  }
                >
                  <div
                    className={
                      "absolute inset-y-0 left-0 rounded-full transition-[width] " +
                      progressClass
                    }
                    style={{
                      width: started || reducedMotion ? "100%" : "0%",
                      transitionDuration: reducedMotion
                        ? "0ms"
                        : track.key === "manual"
                          ? "180000ms" // 3 minutes
                          : track.key === "otherAI"
                            ? "60000ms" // 1 minute
                            : "2000ms", // 2 seconds
                    }}
                    aria-hidden="true"
                  />
                </div>
                {track.key !== "teacherTools" ? (
                  <div className="relative flex flex-wrap items-center justify-between h-full gap-2">
                    {Array.from({ length: track.steps }).map((_, i) => {
                      const stepKey = manualStepKeys[i];
                      const isOtherAI = track.key === "otherAI";
                      const isAiStep = isOtherAI && aiAssistedKeys.has(stepKey);
                      const pillOverride = isAiStep
                        ? " border border-primary/50 bg-primary/10 text-primary"
                        : "";
                      return (
                        <span key={i} className="inline-flex items-center">
                          <span
                            className={
                              "px-2 py-1 text-[11px] rounded-full whitespace-nowrap opacity-0 translate-y-1 transition duration-700 data-[on=true]:opacity-100 data-[on=true]:translate-y-0 relative z-[1] " +
                              pillClass +
                              pillOverride
                            }
                            style={{
                              transitionDelay: reducedMotion
                                ? "0ms"
                                : `${track.key === "manual" ? i * 120 : i * 160}ms`,
                            }}
                            data-on={started}
                          >
                            <span
                              className={
                                "mr-1 inline-flex items-center justify-center w-5 h-5 rounded-full font-semibold text-[10px] shrink-0 " +
                                (track.key === "teacherTools"
                                  ? "bg-white text-dark-green"
                                  : "bg-foreground text-background")
                              }
                              aria-hidden="true"
                            >
                              {i + 1}
                            </span>
                            {t(`tracks.manual.step.${stepKey}`)}
                            {isAiStep && (
                              <MagicWand
                                size={12}
                                weight="bold"
                                className="inline-block ml-1"
                              />
                            )}
                          </span>
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <div className="relative grid grid-cols-[1fr_1fr] gap-x-4 sm:gap-x-6 gap-y-4 items-center py-1 h-full">
                    <div className="col-[1] row-[1] flex items-center justify-start relative z-[1]">
                      <span
                        className="inline-flex items-center h-6 leading-none px-2 py-4 text-[11px] rounded-full whitespace-nowrap border border-white/40 bg-white/10 text-primary-foreground/90 opacity-0 translate-y-1 transition duration-700"
                        style={{
                          opacity: started ? 1 : 0,
                          transform: started
                            ? "translateY(0)"
                            : "translateY(4px)",
                        }}
                      >
                        <span
                          className="mr-1 inline-flex items-center justify-center w-5 h-5 rounded-full font-semibold text-[10px] shrink-0 bg-white text-dark-green"
                          aria-hidden="true"
                        >
                          1
                        </span>
                        {t("tracks.teacherTools.step.topic")}
                      </span>
                    </div>

                    <div className="col-[2] row-[1]  z-[1] relative w-full p-6 border rounded-xl  sm:p-4 border-dashed">
                      <span className="absolute -top-2 -right-2 text-[10px] leading-none px-2 py-1 rounded-full border border-white bg-primary text-primary-foreground/90 shadow-sm">
                        {t("tracks.badge.fastest")}
                      </span>
                      <div className="flex flex-col items-center gap-3 sm:gap-4">
                        {[0, 1, 2].map((idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center h-6 leading-none px-2 py-4 text-[11px] rounded-full whitespace-nowrap border border-white/40 bg-white/10 text-primary-foreground/90 opacity-0 translate-y-1 transition duration-700"
                            style={{
                              opacity: started ? 1 : 0,
                              transform: started
                                ? "translateY(0)"
                                : "translateY(4px)",
                              transitionDelay: reducedMotion
                                ? "0ms"
                                : `${300 + idx * 120}ms`,
                            }}
                          >
                            <span
                              className="mr-1 inline-flex items-center justify-center w-5 h-5 rounded-full font-semibold text-[10px] shrink-0 bg-white text-dark-green"
                              aria-hidden="true"
                            >
                              {idx + 2}
                            </span>
                            {t(
                              `tracks.teacherTools.step.${teacherToolsMidKeys[idx]}`
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="flex flex-col items-center max-w-3xl gap-3 px-4 py-5 mt-10 text-center border rounded-2xl bg-light-green border-border">
        <div className="inline-flex items-center gap-2 text-lg font-semibold text-dark-green">
          <Lightning size={22} weight="bold" />
          {t("tracks.savings", { percent: savedPercent })}
        </div>
        <p className="text-xs text-muted-foreground">{t("tracks.caption")}</p>
      </div>
    </section>
  );
}
